/** @jsxImportSource @emotion/react */
import { CloseButton } from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { BsPlayFill } from "react-icons/bs";
import { IoMdClose } from "react-icons/io";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useResetRecoilState } from "recoil";
import { updateOrderSavedSearch } from "../../../../api/SavedSearches";
import { RecipientAtom } from "../../../../atoms/RecipientAtom";
import { CountedSavedSearchesAtom } from "../../../../atoms/SavedSearches";
import {
  HelpVideoNameMap,
  IHelpContexts,
} from "../../../../types/HelpVideosTypes";
import {
  CountedSavedSearches,
  SavedSearch,
} from "../../../../types/SavedSearchTypes";
import {
  localStorageGetItem,
  localStorageSetItem,
} from "../../../../utils/LocalStorage";
import { HelpVideo } from "../../../HelpVideo";
import Loading from "../../../Loading";
import { useModal } from "../../../Modal/Modal";
import SortableList from "../../../SortableList/SortableList";
import StyledButton from "../../../StyledButton";
import SavedSearchCard from "../../SavedSearchCard";
import RecipientsModal from "../Recipients";
import { DeleteSearchModal } from "../SavedSearch/DeleteSearchModal";
import { Container, Header, HeaderTitle } from "../SavedSearches";

const EmptySavedSearchWrapper = styled.div`
  width: 100%;
  height: 100%;

  margin-top: 56px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;

  .text-wrapper {
    max-width: 320px;
    width: 100%;

    margin: 32px 0;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    grid-gap: 10px;

    text-align: center;

    .title {
      font-size: 20px;
      font-weight: 600;
      color: #2f2f2f;
    }

    .description {
      font-size: 16px;
      font-weight: 400;
      color: #979797;
    }
  }
`;

const Wrapper = styled.div<{ hasGotItBanner?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 0px 24px;
  margin: ${(props) =>
    !props.hasGotItBanner ? "85px 0px 90px" : "82px 0px 90px"};
  grid-gap: 10px;
  margin-bottom: 24px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #2f2f2f;

  margin-bottom: 8px;
`;

const Counter = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #707070;
`;

const MenuOptionsGotIt = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 19px;
  border-radius: 4px;
  border: 1px solid #fb972e;
  background-color: #fff2e5;

  > div > p {
    margin: 0;
    font-size: 14px;
    color: #333333;
    font-weight: 600;
  }

  > div > span {
    color: #707070;
    font-size: 13px;
  }

  > button {
    width: 68px;
    height: 34px;
    padding: 7px 14px;
    font-size: 15px;
  }
`;

const MenuOptions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 45px;
  padding: 12px 19px;
  border-radius: 4px;
  border: 1px solid #fb972e;
  background-color: #fff2e5;
  font-weight: 600;
  font-size: 15px;

  > div > span {
    font-weight: 400;
  }

  > button {
    cursor: pointer;
    background-color: transparent;
    border: none;
    color: #868482;

    :hover {
      background-color: transparent;
    }
  }
`;

interface Props {
  editSavedSearch: (search: SavedSearch) => void;
  editTagsSavedSearch: (search: SavedSearch) => void;
}

export default function ManageSearchModal(props: Props): JSX.Element {
  const modal = useModal();
  const { editSavedSearch, editTagsSavedSearch } = props;

  const [arrayCountedSavedSearches, setArrayCountedSavedSearches] = useState<
    CountedSavedSearches[]
  >([]);
  const [flag, setFlag] = useState(false);

  const [countedSavedSearches, setCountedSavedSearches] = useRecoilState(
    CountedSavedSearchesAtom
  );

  const resetRecipientAtom = useResetRecoilState(RecipientAtom);

  function handleSearchDelete(search: SavedSearch) {
    modal.pushModal(<DeleteSearchModal search={search} />);
  }

  const cachedGotIt =
    JSON.parse(`${localStorageGetItem("MANAGE_SAVED_SEARCHES_GOT_IT")}`) ||
    false;

  const [gotIt, setGotIt] = useState(cachedGotIt);

  useEffect(() => {
    if (cachedGotIt) return;
    localStorageSetItem("MANAGE_SAVED_SEARCHES_GOT_IT", JSON.stringify(gotIt));
  }, [gotIt]);

  // function handleSearchFooter(search: SavedSearch) {
  //   modal.pushModal(
  //     <FooterSearchModal search={search} />,
  //     "Footer for this search"
  //   );
  // }

  // useEffect(() => {
  //   resetRecipientAtom();
  // }, []);

  useEffect(() => {
    if (countedSavedSearches) {
      setArrayCountedSavedSearches(countedSavedSearches);
    }
    setFlag(!flag);
  }, [countedSavedSearches]);

  async function handleUpdateOrderSavedSearches(
    newOrderArray: CountedSavedSearches[]
  ) {
    if (!newOrderArray.length) return;

    try {
      let newOrder = newOrderArray.map((search) => search.id);
      const response = await updateOrderSavedSearch({ newOrder });

      if (response) {
        setCountedSavedSearches(newOrderArray);
      }
    } catch (error) {
      console.log("Failed to update the saved search order: ", error);
    }
  }

  const GotItBanner = React.useMemo(() => {
    return (
      <>
        {/* {!gotIt && (
          <MenuOptionsGotIt>
            <div>
              <p>Menu Options</p>
              <span>
                Hover over your saved searches to see the menu options
              </span>
            </div>
            <StyledButton onClick={() => setGotIt(true)}>Got it</StyledButton>
          </MenuOptionsGotIt>
        )} */}
        {!gotIt && (
          <MenuOptions>
            <div>
              Note:{" "}
              <span>
                Hover over your saved searches to see the menu options
              </span>
            </div>
            <CloseButton onClick={() => setGotIt(true)} />
          </MenuOptions>
        )}
      </>
    );
  }, [gotIt]);

  return (
    <Container>
      <Header>
        <HeaderTitle>Manage Saved Searches</HeaderTitle>
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <div
            className="icon-button"
            onClick={() =>
              modal.pushModal(
                <HelpVideo contextTag={IHelpContexts.ManageSavedSearches} />,
                HelpVideoNameMap[IHelpContexts.ManageSavedSearches]
              )
            }
            data-tip
            data-for="openVideo"
          >
            <BsPlayFill />
            <ReactTooltip
              id="openVideo"
              place="bottom"
              effect="solid"
              globalEventOff="click"
              backgroundColor="#333333"
              css={css`
                width: fit-content !important;
                padding: 6px 8px !important;
              `}
            >
              <div className="tooltip-text">Watch Quick Help Video</div>
            </ReactTooltip>
          </div>

          <IoMdClose
            css={css`
              cursor: pointer;
            `}
            color="#868482"
            size={25}
            onClick={() => {
              modal.popModal();
            }}
          />
        </div>
      </Header>

      <Wrapper hasGotItBanner={gotIt} key={`${flag}`}>
        {GotItBanner}
        {countedSavedSearches ? (
          countedSavedSearches.length > 0 ? (
            <SortableList
              items={arrayCountedSavedSearches}
              onDragEnd={(items) => {
                handleUpdateOrderSavedSearches(items);
              }}
            >
              {arrayCountedSavedSearches.map((search, index) => (
                <SavedSearchCard
                  key={`saved_search_card_${search.id}`}
                  id={index.toString()}
                  search={search}
                  editSavedSearch={() => editSavedSearch(search)}
                  editTagsSavedSearch={() => editTagsSavedSearch(search)}
                  handleSearchDelete={() => handleSearchDelete(search)}
                />
              ))}
            </SortableList>
          ) : (
            <EmptySavedSearchWrapper>
              <img src="/saved-searches-empty.svg" alt="Empty" />

              <span className="text-wrapper">
                <div className="title">No Saved Searches To Show</div>
                <div className="description">
                  You currently have no saved searches. Select contacts to start
                  saving searches.
                </div>
              </span>

              <StyledButton
                onClick={() => {
                  modal.popModal();
                  modal.pushModal(<RecipientsModal />, "Contacts");
                }}
              >
                Select Contacts
              </StyledButton>
            </EmptySavedSearchWrapper>
          )
        ) : (
          <Loading />
        )}
      </Wrapper>
    </Container>
  );
}
